function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)
}

/**
 * Form values and loaded home values often differ only by empty-string vs null.
 * Those are the same stored value and must not be sent as an edit.
 */
export function formValuesEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) return true
    if (left == null && right == null) return true
    if (left == null) return right === ''
    if (right == null) return left === ''

    if (typeof left === 'number' && typeof right === 'string') {
        return right.trim() !== '' && Number(right) === left
    }
    if (typeof right === 'number' && typeof left === 'string') {
        return left.trim() !== '' && Number(left) === right
    }

    if (left instanceof Date || right instanceof Date) {
        const leftTime = left instanceof Date ? left.getTime() : Date.parse(String(left))
        const rightTime = right instanceof Date ? right.getTime() : Date.parse(String(right))
        return !Number.isNaN(leftTime) && leftTime === rightTime
    }

    if (Array.isArray(left) || Array.isArray(right) || (isPlainObject(left) && isPlainObject(right))) {
        return JSON.stringify(left) === JSON.stringify(right)
    }

    return false
}

/** Drop payload keys whose form value still matches the last loaded value. */
export function omitUnchangedFields<T extends Record<string, unknown>>(
    payload: T,
    current: Record<string, unknown> | null | undefined,
    baseline: Record<string, unknown> | null | undefined,
): T {
    const next = { ...payload }
    for (const key of Object.keys(next)) {
        if (formValuesEqual(current?.[key], baseline?.[key])) {
            delete next[key]
        }
    }
    return next
}
